/**
 * ticket_pipeline — every vertical.
 *
 * CASING ALONE reads GET /dashboard/ticket-pipeline, rendered as sent, exactly
 * as before: an unpriced status is "—"; a failed read is a failure notice,
 * never seven bars at zero.
 *
 * A COMPANY WITH ANY OTHER VERTICAL reads GET /dashboard/pipeline (api#1136):
 * one block per vertical (VerticalPipelineBlock), casing's figures equal to
 * /ticket-pipeline's. The header's Tickets link is casing's list, so it is
 * drawn only for a company that has casing; every block links to its own list.
 */
import TicketPipeline from '../../dashboard/TicketPipeline';
import { LoadFailureNotice } from '../../ui/LoadFailureNotice';
import { queryFailureError, queryLoadFailure } from '../../../lib/load-failure';
import { WidgetPanel, WidgetPending } from '../WidgetPanel';
import { usePipeline, useTicketPipeline } from '../overviewQueries';
import { useAuth } from '../../../contexts/AuthContext';
import { hasGlobalMoneyVertical } from '../globalMoney';
import { VerticalPipelineBlock } from '../VerticalPipelineBlock';

function GlobalTicketPipeline() {
  const { hasVertical } = useAuth();
  const q = usePipeline(true);
  const failure = q.data === undefined ? queryLoadFailure(q) : null;
  return (
    <WidgetPanel
      widgetKey="ticket_pipeline"
      eyebrow="Field to invoice"
      title="Ticket Pipeline"
      link={hasVertical('casing_running') ? { label: 'Tickets', to: '/tickets' } : undefined}
    >
      <div className="p-4">
        {failure ? (
          <LoadFailureNotice error={queryFailureError(q)} kind={failure} subject="the ticket pipeline" className="py-6" allowed="admins, office staff, supervisors and dispatchers" />
        ) : q.data === undefined ? (
          <WidgetPending rows={4} />
        ) : q.data === null ? (
          // An envelope with no payload is not an empty pipeline.
          <LoadFailureNotice error={new Error('No pipeline payload')} kind="unreachable" subject="the ticket pipeline" className="py-6" />
        ) : (
          <div className="space-y-5">
            {q.data.verticals.map((v) => <VerticalPipelineBlock key={v.vertical} v={v} />)}
          </div>
        )}
      </div>
    </WidgetPanel>
  );
}

export default function TicketPipelineWidget() {
  const { hasVertical } = useAuth();
  return hasGlobalMoneyVertical(hasVertical) ? <GlobalTicketPipeline /> : <CasingTicketPipeline />;
}

function CasingTicketPipeline() {
  const q = useTicketPipeline();
  const failure = q.data === undefined ? queryLoadFailure(q) : null;
  const statuses = q.data?.statuses ?? null;

  return (
    <WidgetPanel widgetKey="ticket_pipeline" eyebrow="Field to invoice" title="Ticket Pipeline" link={{ label: 'Tickets', to: '/tickets' }}>
      <div className="p-4">
        {failure ? (
          <LoadFailureNotice error={queryFailureError(q)} kind={failure} subject="the ticket pipeline" className="py-6" allowed="admins, office staff, supervisors and dispatchers" />
        ) : q.isLoading ? (
          <WidgetPending rows={4} />
        ) : !statuses ? (
          <p className="py-6 text-center text-sm text-fieldiq-muted">—</p>
        ) : // A loaded payload with no ticket in any status is genuinely "none".
          statuses.every((s) => s.count === 0) ? (
          <p data-testid="pipeline-empty" className="py-6 text-center text-sm text-fieldiq-muted">No tickets yet.</p>
        ) : (
          <TicketPipeline statuses={statuses} />
        )}
      </div>
    </WidgetPanel>
  );
}
